import { useParams, Link } from 'react-router-dom'
import { Shield, TrendingUp, Heart, Laptop } from 'lucide-react'
import { SiteNav, SiteFooter } from '@/components/ui/SiteNav'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { INDUSTRY_TO_DEMO_ID } from '@/lib/demo/constants'
import type React from 'react'

interface IndustryData {
  Icon: React.ComponentType<{ className?: string; strokeWidth?: string | number }>
  heroH1: string
  heroSub: string
  useCases: Array<{ title: string; desc: string }>
  buyers: Array<{ role: string; desc: string }>
  signalsSection: { title: string; bullets: string[]; disclaimer: string }
  faqs: Array<{ q: string; a: string }>
  ctaText: string
}

const INDUSTRIES: Record<string, IndustryData> = {
  insurance: {
    Icon: Shield,
    heroH1: 'Pre-read insurance communications for clarity, trust, and review risk before fielding.',
    heroSub: 'Echosome predicts how policyholders, claimants, and brokers may react to renewal notices, claims communication, agent scripts, and quote-flow copy. Outputs flag language patterns associated with NAIC bulletin guidance and state-specific complaint trends. Final review stays with your legal and compliance teams.',
    useCases: [
      { title: 'Renewal notices', desc: 'Concrete causation vs. vague language patterns.' },
      { title: 'Claims communication', desc: 'Empathetic language with awareness of admission-of-liability phrasings.' },
      { title: 'Agent scripts', desc: 'Trust-building opening and objection handling per customer segment.' },
      { title: 'Deductible explanations', desc: 'Plain-language clarity for first-time policyholders.' },
      { title: 'Quote-flow trust signals', desc: 'What converts a quote into a purchase, segment by segment.' },
      { title: 'Broker enablement copy', desc: 'Pre-read how brokers may receive carrier messaging.' },
      { title: 'Per-customer renewal reaction', desc: 'Build a twin panel from prior survey wave. Predict per-policyholder reactions to renewal framing before committing production copy.' },
    ],
    buyers: [
      { role: 'Head of Compliance', desc: 'Surface review-risk signals before legal review.' },
      { role: 'VP of Customer Experience', desc: 'Predict policyholder reactions before fielding.' },
      { role: 'Director of Communications', desc: 'Ship more campaigns per quarter with fewer review delays.' },
    ],
    signalsSection: {
      title: 'What Echosome flags in insurance research',
      bullets: [
        'NAIC Model Bulletin 1812 (premium notice clarity) language patterns',
        'AICPCU best-practice guidance alignment signals',
        'State-specific complaint trend language patterns from public NAIC databases',
        'Plain-language readability signals per literacy band',
      ],
      disclaimer: 'These are research signals to inform your review process. They are not compliance approvals. Final review stays with your legal and compliance teams. Echosome does not provide legal advice. Echosome is decision support, not regulated approval.',
    },
    faqs: [
      { q: 'How does Echosome flag state-specific issues?', a: 'Pattern recognition is based on public NAIC complaint database trends and state regulatory bulletins. These are research signals; state-by-state legal interpretation stays with your counsel.' },
      { q: 'Does it work for commercial lines as well as personal lines?', a: 'Yes. The approach is configurable for the segment and policy type your data contains.' },
      { q: 'Can we use this for claims communication?', a: 'Yes, with the caveat that Echosome flags language patterns; it does not substitute for adjuster judgment or legal review of admissions risk.' },
      { q: 'How does it handle policyholder PII?', a: 'PII is detected at upload and flagged before any external model call. Default demo mode never sends data to external providers. Enterprise deployments support stricter data residency configurations.' },
      { q: 'Can we run pre-reads on broker-facing copy as well as policyholder copy?', a: 'Yes — broker is a supported segment type.' },
      { q: 'Can Echosome do per-customer modeling?', a: 'Yes — the Twin Panel module builds a 1:1 digital twin of each respondent from uploaded survey data. Each twin is backtested against 20% of held-out actual answers and reported with a Wilson 95% confidence interval. Bulk queries let you ask new questions across the full panel and return a predicted distribution + per-segment cross-tab in ~40 seconds. Twins below the confidence floor are excluded by default.' },
    ],
    ctaText: 'Pre-read your next renewal campaign in under 60 seconds.',
  },
  'financial-services': {
    Icon: TrendingUp,
    heroH1: 'Pre-read financial services communications for clarity, plain-language, and review-risk signals before fielding.',
    heroSub: 'Echosome predicts how customers may react to fee disclosures, account-opening flows, advisor scripts, and trust-sensitive communications. Outputs flag clarity, disclosure-language, and review-risk patterns related to financial-services communication workflows. Final review stays with your legal and compliance teams.',
    useCases: [
      { title: 'Fee disclosures', desc: 'Plain-language signals per customer segment.' },
      { title: 'Account-opening flows', desc: 'Where customers drop off, and what copy keeps them on.' },
      { title: 'Advisor scripts', desc: 'Suitability-aware language by client segment.' },
      { title: 'Loan or credit-card application friction', desc: 'Disclosure-aware copy that maintains conversion.' },
      { title: 'Trust and risk communication', desc: 'Language patterns associated with adverse-action notice clarity.' },
      { title: 'Onboarding and welcome emails', desc: 'First-touch comprehension by literacy band.' },
      { title: 'Per-customer disclosure reaction', desc: 'Build a twin panel from prior wave. Predict per-customer reactions to disclosure framing options before committing to field.' },
    ],
    buyers: [
      { role: 'VP of Compliance', desc: 'Surface plain-language and disclosure-language signals before legal review.' },
      { role: 'Head of CX or Customer Communications', desc: 'Faster ship cycles on trust-sensitive copy with informed signals.' },
      { role: 'Product Manager (account-opening / onboarding)', desc: 'Pre-read flow copy before A/B testing live.' },
    ],
    signalsSection: {
      title: 'What Echosome flags in financial-services research',
      bullets: [
        'CFPB plain-language guidance and Regulation Z disclosure formatting patterns',
        'Regulation B §1002.9 adverse-action statement language patterns',
        'Truth-in-Savings disclosure language patterns',
        'FINRA suitability-language signals',
        'State-level UDAAP trigger-language patterns from public sources',
      ],
      disclaimer: 'These are research signals to inform your review process. They are not compliance approvals. Important: Echosome is decision support only. It is not for credit decisions, adverse action, investment recommendations, or any regulated approval workflow. Final review stays with your legal and compliance teams.',
    },
    faqs: [
      { q: 'Does Echosome compute a reading-grade level for every message?', a: 'Yes — Flesch-Kincaid and related readability scores are surfaced per segment. These are research signals, not regulatory determinations.' },
      { q: 'Can we use this on adverse-action copy?', a: 'You can use Echosome to surface clarity and disclosure-language signals in adverse-action communications. Final review of adverse-action language must stay with your legal and compliance teams.' },
      { q: 'How is customer PII handled before any LLM call?', a: 'PII is detected and flagged at upload. Default demo mode never sends uploaded data to external LLM providers. Enterprise plans support stricter data-residency configurations.' },
      { q: 'Does it work for advisor scripts and oral communications?', a: 'Yes for written or transcribed scripts. Oral communication compliance remains with your supervisory framework.' },
      { q: 'Can this replace legal review?', a: 'No. Echosome surfaces research signals. Final legal and compliance review stays with your team.' },
      { q: 'Can Echosome do per-customer modeling?', a: 'Yes — the Twin Panel module builds a 1:1 digital twin of each respondent from uploaded survey data. Each twin is backtested against 20% of held-out actual answers and reported with a Wilson 95% confidence interval. Bulk queries return predicted distributions + per-segment cross-tabs in ~40 seconds. Twins below the confidence floor are excluded by default.' },
    ],
    ctaText: 'Pre-read your next fee disclosure in under 60 seconds.',
  },
  healthcare: {
    Icon: Heart,
    heroH1: 'Pre-read patient and member communications for comprehension before rollout.',
    heroSub: 'Echosome predicts how plan members and patients may understand and respond to communications, segmented by literacy band. Outputs flag readability, medical-jargon density, and comprehension-risk signals. Final review stays with your clinical, compliance, and communications teams.',
    useCases: [
      { title: 'Appointment reminders', desc: 'Per-literacy-band readability signals.' },
      { title: 'Benefits comprehension', desc: 'Which benefit explanations members may understand by segment.' },
      { title: 'Portal activation messages', desc: 'First-touch enrollment language by demographic.' },
      { title: 'Digital health onboarding', desc: 'App download and first-use messaging clarity.' },
      { title: 'Care-plan engagement', desc: 'Reminder, refill, and adherence message clarity.' },
      { title: 'Member retention messaging', desc: 'Open-enrollment and renewal communication clarity.' },
      { title: 'Per-member comprehension modeling', desc: 'Build a twin panel from prior survey wave. Predict per-member reactions to plan communication changes across literacy bands.' },
    ],
    buyers: [
      { role: 'VP of Member Experience', desc: 'Surface comprehension signals across literacy bands before rollout.' },
      { role: 'Head of Patient Communications', desc: 'Plain-language rewrites with research signals to inform review.' },
      { role: 'Director of Digital Engagement', desc: 'Pre-read app messaging before launch.' },
    ],
    signalsSection: {
      title: 'What Echosome flags in healthcare research',
      bullets: [
        'Flesch-Kincaid, SMOG, and related readability scoring',
        'Medical jargon density against plain-language dictionaries',
        'Pediatric vs. geriatric phrasing appropriateness signals',
      ],
      disclaimer: 'These are research signals to inform your communication review. Important safety note: Echosome supports research and communication analysis. It is not a clinical decision system. It does not interpret diagnostic codes for clinical use. It does not handle PHI for treatment purposes. PII is redacted before any external model call by default. Final review stays with your clinical, compliance, and communications teams.',
    },
    faqs: [
      { q: 'Can Echosome handle PHI?', a: 'Default demo mode never sends uploaded data to external models. For real PHI workflows, the Enterprise plan supports private deployments and DPAs. Final responsibility for PHI handling stays with your covered-entity team.' },
      { q: 'How is literacy band determined from the data?', a: 'Echosome infers literacy band from optional demographic columns you provide (such as education or proxy fields). When that data isn\'t present, the pack defaults to a single un-segmented readability view.' },
      { q: 'What readability signals does Echosome provide?', a: 'A readability-and-comprehension signal indicating which messages may be harder for low-literacy members to understand. It is a research signal, not a regulatory determination.' },
      { q: 'Can this be used for clinical decision-support?', a: 'No. Echosome is for research and communication analysis only.' },
      { q: 'Does the plain-language rewrite preserve clinical accuracy?', a: 'Plain-language rewrites are research drafts. Clinical accuracy review stays with your clinical team.' },
      { q: 'Can Echosome do per-member modeling?', a: 'Yes — the Twin Panel module builds a 1:1 digital twin of each respondent from uploaded survey data. Each twin is backtested against 20% of held-out actual answers and reported with a Wilson 95% confidence interval. Bulk queries return predicted distributions + per-literacy-band cross-tabs in ~40 seconds. Twins below the confidence floor are excluded by default.' },
    ],
    ctaText: 'Pre-read your next member communication in under 60 seconds.',
  },
  saas: {
    Icon: Laptop,
    heroH1: 'Pre-read your retention spend allocation — by checking churn reasons against behavior.',
    heroSub: 'Echosome joins your churn surveys with your behavioral data and outcome data to show which stated reasons align with what customers actually did. Most SaaS churn surveys overstate price by 15–30 percentage points. Echosome shows you where the gap is.',
    useCases: [
      { title: 'Churn reason validation', desc: 'Stated vs. behavioral attribution, with per-cohort gaps.' },
      { title: 'Upgrade prompt copy', desc: 'Which message lands with which segment.' },
      { title: 'Pricing page tests', desc: 'How segments may react to pricing changes before A/B.' },
      { title: 'Onboarding friction mapping', desc: 'Where activation appears to fail, segment by segment.' },
      { title: 'Feature adoption messaging', desc: 'Which feature framing lands with which user type.' },
      { title: 'Customer advisory board prep', desc: 'Synthesize prior interviews to focus the live agenda.' },
      { title: 'Per-customer churn-reason modeling', desc: 'Build a twin panel from your prior survey wave. Ask new questions about churn drivers per customer — get predicted distribution + per-cohort cross-tab.' },
    ],
    buyers: [
      { role: 'Head of Retention', desc: 'Reallocate retention spend based on signals from validated drivers.' },
      { role: 'VP of Product', desc: 'Pre-read upgrade and onboarding copy by segment before A/B testing live.' },
      { role: 'Head of Customer Success', desc: 'Surface early-warning behavioral signatures associated with churn.' },
    ],
    signalsSection: {
      title: 'Why stated churn reasons are unreliable on their own',
      bullets: [
        'Price is overcited because it\'s socially acceptable — customers lead with it even when behavior says otherwise',
        'Feature gaps underreported — customers don\'t know what they didn\'t use',
        'Activation failure invisible in exit surveys — customers who never activated don\'t churn, they ghost',
      ],
      disclaimer: 'Echosome surfaces signals from your combined survey and behavioral data. Final retention strategy decisions stay with your team. Echosome is decision support, not a replacement for domain expertise.',
    },
    faqs: [
      { q: 'Can I use this without giving Echosome access to my data warehouse?', a: 'Yes — upload CSV exports. No live integration required.' },
      { q: 'How many churned customers do I need before this works?', a: 'Useful results start at 50 churned customers with matched behavioral data. Best results at 200+.' },
      { q: 'Does this work for product-led growth (PLG) as well as sales-led?', a: 'Yes — segment definitions adapt to either GTM model.' },
      { q: 'Does it handle B2B SaaS as well as consumer subscription?', a: 'Yes, with segment definitions adjusted accordingly.' },
      { q: 'Can I validate the gap with real humans before reallocating budget?', a: 'Yes — that\'s the recommended workflow. Echosome can recruit real humans through your panel provider for the segments where signals are weak.' },
      { q: 'Can Echosome do per-customer modeling?', a: 'Yes — the Twin Panel module builds a 1:1 digital twin of each customer from uploaded survey data. Each twin is backtested against 20% of held-out actual answers and reported with a Wilson 95% confidence interval. Bulk queries return predicted churn-driver distributions + per-cohort cross-tabs in ~40 seconds. Twins below the confidence floor are excluded by default.' },
    ],
    ctaText: 'See the churn gap in your own data.',
  },
}

export function IndustryPage() {
  const { industry } = useParams<{ industry: string }>()
  const data = INDUSTRIES[industry ?? '']
  const demoId = INDUSTRY_TO_DEMO_ID[industry ?? ''] ?? 'saas-churn'

  if (!data) {
    return (
      <div className="min-h-screen bg-paper">
        <SiteNav />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <p className="text-slate-500">Industry page not found.</p>
          <Link to="/"><Button variant="outline" size="sm" className="mt-4">Back to home</Button></Link>
        </div>
        <SiteFooter />
      </div>
    )
  }

  const { Icon } = data

  return (
    <div className="min-h-screen bg-paper">
      <SiteNav />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-16 space-y-16">

        {/* Hero */}
        <div className="space-y-5 max-w-3xl">
          <div className="flex items-center gap-2">
            <Icon className="h-5 w-5 text-echo-indigo" strokeWidth={1.75} />
          </div>
          <h1 className="text-4xl font-medium text-deep-ink leading-tight" style={{ letterSpacing: '-0.02em' }}>{data.heroH1}</h1>
          <p className="text-lg text-slate-600 leading-relaxed">{data.heroSub}</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to={`/app/demo?industry=${demoId}`}
              data-testid="try-live-demo-cta"
              data-industry={demoId}
              data-cta="primary"
            >
              <Button size="lg" variant="primary">Try the live demo (60s)</Button>
            </Link>
            <Link to="/signup" data-cta="secondary">
              <Button size="lg" variant="outline">Start with your own data</Button>
            </Link>
          </div>
        </div>

        {/* Use cases */}
        <div className="space-y-4">
          <h2 className="text-xl font-medium text-deep-ink">Use cases</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {data.useCases.map(uc => (
              <Card key={uc.title} className="space-y-1">
                <p className="font-medium text-deep-ink text-sm">{uc.title}</p>
                <p className="text-sm text-slate-600">{uc.desc}</p>
              </Card>
            ))}
          </div>
        </div>

        {/* Who buys this */}
        <div className="space-y-4">
          <h2 className="text-xl font-medium text-deep-ink">Who uses Echosome here</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {data.buyers.map(b => (
              <Card key={b.role} className="space-y-1">
                <p className="font-medium text-deep-ink text-sm">{b.role}</p>
                <p className="text-sm text-slate-600">{b.desc}</p>
              </Card>
            ))}
          </div>
        </div>

        {/* Review-risk signals section */}
        <div className="bg-soft-panel rounded-xl border border-border-gray p-6 space-y-4">
          <h2 className="text-lg font-medium text-deep-ink">{data.signalsSection.title}</h2>
          <ul className="space-y-2">
            {data.signalsSection.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <span className="text-echo-indigo mt-0.5 shrink-0">→</span>
                {b}
              </li>
            ))}
          </ul>
          <p className="text-sm text-slate-500 italic border-t border-border-gray pt-3">{data.signalsSection.disclaimer}</p>
        </div>

        {/* FAQ */}
        <div className="space-y-4">
          <h2 className="text-xl font-medium text-deep-ink">Common questions</h2>
          <div className="space-y-3">
            {data.faqs.map((faq, i) => (
              <div key={i} className="border border-border-gray rounded-xl p-4 space-y-1">
                <p className="font-medium text-deep-ink text-sm">{faq.q}</p>
                <p className="text-sm text-slate-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <div className="bg-echo-indigo/5 border border-echo-indigo/20 rounded-xl p-8 text-center space-y-4">
          <h2 className="text-xl font-medium text-deep-ink">{data.ctaText}</h2>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={`/app/demo?industry=${demoId}`} data-cta="primary">
              <Button variant="primary" size="lg">See the live demo</Button>
            </Link>
            <Link to="/signup" data-cta="secondary">
              <Button variant="outline" size="lg">Start with your own data</Button>
            </Link>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  )
}
